/**
 * useAppointmentConflicts.js
 *
 * Choque de horário do novo agendamento, conferido no servidor enquanto o
 * formulário é preenchido (check_conflicts.php): a mesma agenda, a sala cheia ou
 * a mesma pessoa já têm atendimento que se sobrepõe ao horário.
 *
 * Serve para o aviso no campo Sala e para perguntar "Encaixar mesmo assim?" ainda
 * no clique de salvar (o WhatsApp Web só abre dentro do clique). Ao salvar, o
 * servidor confere de novo. Falha na consulta = lista vazia: nunca bloqueia.
 */

import { useEffect, useState } from 'react';
import { CheckAppointmentConflictsAPI } from '../../../../../api/API';

const DEBOUNCE_MS = 350;

/**
 * @param {Object} params
 * @param {boolean} params.enabled
 * @param {string} params.date - "YYYY-MM-DD HH:MM:SS"
 * @param {string} params.duration - "HH:MM:SS"
 * @param {string} params.resource
 * @param {string|null} params.roomId
 * @param {string|number|null} params.patient
 * @returns {Array} [{ type: 'resource'|'room'|'patient', id, start, end, resourceName, roomName }]
 */
export default function useAppointmentConflicts({ enabled, date, duration, resource, roomId, patient }) {
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (!enabled || !date || (!resource && !roomId && (patient == null || patient === ''))) {
      setConflicts([]);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      CheckAppointmentConflictsAPI({ date, duration, resource, roomId, patient }, (response) => {
        if (!active) return;
        const list = response?.status === 'SUCCESS' ? response?.data?.conflicts : null;
        setConflicts(Array.isArray(list) ? list : []);
      });
    }, DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [enabled, date, duration, resource, roomId, patient]);

  return conflicts;
}

/** Um choque por atendimento (o mesmo pode chocar como agenda, sala e pessoa) */
export function uniqueConflicts(conflicts) {
  const seen = new Set();
  return (conflicts || []).filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
}

/** "Agenda Ana já tem atendimento das 10:00 às 11:00", "Sala 2 em uso por Ana das ..." */
export function describeConflict(conflict) {
  const when = `das ${conflict.start} às ${conflict.end}`;
  if (conflict.type === 'room') {
    const who = conflict.resourceName ? ` por ${conflict.resourceName}` : '';
    return `${conflict.roomName || 'Sala'} em uso${who} ${when}`;
  }
  if (conflict.type === 'patient') {
    const where = conflict.resourceName ? ` na agenda ${conflict.resourceName}` : '';
    return `A pessoa já tem atendimento${where} ${when}`;
  }
  const where = conflict.resourceName ? ` ${conflict.resourceName}` : '';
  return `Agenda${where} já tem atendimento ${when}`;
}
